export const EMAIL_MONITOR_PAGE_SIZES = [10, 20, 50, 100];

export const EMAIL_MONITOR_DEFAULT_DAYS = 30;

export const EMAIL_MONITOR_MAX_RANGE_DAYS = 90;

export const EMAIL_MONITOR_EVENT_TYPES = [
    { value: '', labelKey: 'all' },
    { value: 'delivered', labelKey: 'delivered' },
    { value: 'opened', labelKey: 'opened' },
    { value: 'open', labelKey: 'open' },
    { value: 'clicked', labelKey: 'clicked' },
    { value: 'hard_bounce', labelKey: 'hardBounce' },
    { value: 'soft_bounce', labelKey: 'softBounce' },
    { value: 'blocked', labelKey: 'blocked' },
    { value: 'spam_report', labelKey: 'spam' },
    { value: 'invalid', labelKey: 'invalid' },
    { value: 'unsubscribed', labelKey: 'unsubscribed' },
];

/** Clés normalisées (sans _/-) → valeur query Brevo */
const BREVO_EVENT_FILTER_MAP = {
    delivered: 'delivered',
    opened: 'opened',
    open: 'opened',
    opens: 'opened',
    clicked: 'clicked',
    hardbounce: 'hardBounces',
    hardbounces: 'hardBounces',
    softbounce: 'softBounces',
    softbounces: 'softBounces',
    blocked: 'blocked',
    spam: 'spam',
    spamreport: 'spam',
    invalid: 'invalid',
    unsubscribed: 'unsubscribed',
};

const normalizeEventLookupKey = (value) =>
    String(value || '').trim().toLowerCase().replace(/[_-]/g, '');

/**
 * Normalise un filtre événement UI → valeur Brevo (hardBounces, opened, …).
 * @throws {Error} si valeur inconnue (équivalent 422 backend)
 */
export const resolveBrevoEventFilter = (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return null;
    const key = normalizeEventLookupKey(trimmed);
    const resolved = BREVO_EVENT_FILTER_MAP[key];
    if (!resolved) {
        const error = new Error(`Invalid event filter: ${trimmed}`);
        error.code = 'INVALID_EVENT_FILTER';
        throw error;
    }
    return resolved;
};

/** Réponse API Brevo → clé i18n events.* */
export const brevoEventToLabelKey = (event) => {
    const key = normalizeEventLookupKey(event);
    const labelMap = {
        delivered: 'delivered',
        opened: 'opened',
        open: 'open',
        clicked: 'clicked',
        hardbounce: 'hardBounce',
        hardbounces: 'hardBounce',
        softbounce: 'softBounce',
        softbounces: 'softBounce',
        blocked: 'blocked',
        spam: 'spam',
        spamreport: 'spam',
        invalid: 'invalid',
        unsubscribed: 'unsubscribed',
    };
    return labelMap[key] || null;
};

export const isValidBrevoEventFilter = (raw) => {
    if (!String(raw || '').trim()) return true;
    try {
        resolveBrevoEventFilter(raw);
        return true;
    } catch {
        return false;
    }
};

/** email, template_id ou message_id — requis avec le filtre subject */
export const hasEmailListAnchorFilter = ({ email, templateId, messageId }) => {
    const trimmedEmail = String(email || '').trim();
    const trimmedMessageId = String(messageId || '').trim();
    const template = templateId != null && String(templateId).trim() !== ''
        ? String(templateId).trim()
        : '';
    return Boolean(trimmedEmail || trimmedMessageId || template);
};

export const validateEmailMonitorListFilters = ({
    email,
    templateId,
    messageId,
    subject,
    eventType,
}) => {
    const trimmedSubject = String(subject || '').trim();
    const trimmedEvent = String(eventType || '').trim();

    if (trimmedSubject && trimmedEvent) {
        return { valid: false, code: 'subjectEventIncompatible' };
    }
    if (trimmedSubject && !hasEmailListAnchorFilter({ email, templateId, messageId })) {
        return { valid: false, code: 'subjectRequiresAnchor' };
    }
    return { valid: true };
};

export const getDefaultEmailMonitorDateRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - EMAIL_MONITOR_DEFAULT_DAYS);
    const toIso = (d) => d.toISOString().slice(0, 10);
    return { date_from: toIso(from), date_to: toIso(to) };
};

export const formatEmailMonitorDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('fr-FR', {
            timeZone: 'Europe/Paris',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

export const truncateEmailUuid = (uuid) => {
    if (!uuid || uuid.length < 12) return uuid || '—';
    return `${uuid.slice(0, 8)}…${uuid.slice(-4)}`;
};

export const truncateMessageId = (messageId, max = 40) => {
    const text = messageId || '';
    if (!text) return '—';
    if (text.length <= max) return text;
    return `${text.slice(0, max)}…`;
};

export const getEmailEventBadgeVariant = (event) => {
    const e = normalizeEventLookupKey(event);
    if (e === 'delivered') return 'success';
    if (e === 'opened' || e === 'open' || e === 'opens' || e === 'clicked') return 'primary';
    if (e === 'hardbounce' || e === 'hardbounces' || e === 'spam' || e === 'spamreport' || e === 'invalid') {
        return 'danger';
    }
    if (e === 'softbounce' || e === 'softbounces' || e === 'blocked') return 'warning';
    return 'secondary';
};

export const formatEmailStatsRange = (range) => {
    if (!range) return null;
    const start = range.start_date || range.startDate;
    const end = range.end_date || range.endDate;
    if (!start && !end) return null;
    return `${start || '—'} → ${end || '—'}`;
};

export const truncateEmailSubject = (subject, max = 60) => {
    const text = subject || '';
    if (!text) return '—';
    if (text.length <= max) return text;
    return `${text.slice(0, max)}…`;
};

export const EMAIL_MONITOR_EXPORT_MAX_PAGES = 100;

/**
 * Indique s'il existe une page suivante.
 * - Avec `total` Brevo (liste emails identifiée) : page × page_size < total
 * - Sans total (mode event / agrégat période) : comme SMS — page pleine ⇒ page suivante possible
 */
export const getEmailMonitorHasNextPage = ({
    items,
    page,
    pageSize,
    total,
    hasMore,
    isEventListMode = false,
    isSubjectFilterMode = false,
}) => {
    const count = Array.isArray(items) ? items.length : 0;
    if (count === 0) return false;

    if (isSubjectFilterMode && hasMore != null) {
        return Boolean(hasMore);
    }

    if (!isEventListMode && total != null) {
        if (count < pageSize) return false;
        return page * pageSize < total;
    }

    return count >= pageSize;
};

/** Plage affichée pour la page courante (optionnel si total connu). */
export const getEmailMonitorPageRange = ({ page, pageSize, total, itemCount }) => {
    if (!itemCount) return null;
    const from = (page - 1) * pageSize + 1;
    const to = (page - 1) * pageSize + itemCount;
    return { from, to, total: total ?? null };
};

export const buildEmailMonitorExcelRows = (items, { isEventMode = false } = {}) => {
    if (!Array.isArray(items)) return [];
    if (isEventMode) {
        return items.map((item) => ({
            Date: formatEmailMonitorDate(item.date),
            Destinataire: item.email || '',
            Événement: item.event || '',
            Tag: item.tag || '',
            'Template ID': item.template_id ?? '',
            Motif: item.reason || '',
            'Message-ID': item.message_id || '',
        }));
    }
    return items.map((item) => ({
        UUID: item.uuid || '',
        Date: formatEmailMonitorDate(item.date),
        Destinataire: item.email || '',
        Sujet: item.subject || '',
        'Template ID': item.template_id ?? '',
        'Message-ID': item.message_id || '',
    }));
};

export const fetchAllEmailMonitorItems = async (appliedQuery, fetchPage) => {
    const allItems = [];
    let page = 1;
    const pageSize = 100;

    while (page <= EMAIL_MONITOR_EXPORT_MAX_PAGES) {
        const data = await fetchPage({
            page,
            page_size: pageSize,
            client_email: appliedQuery.email || undefined,
            template_id: appliedQuery.templateId || undefined,
            message_id: appliedQuery.messageId || undefined,
            subject: appliedQuery.subject || undefined,
            event_type: appliedQuery.eventType || undefined,
            date_from: appliedQuery.dateFrom || undefined,
            date_to: appliedQuery.dateTo || undefined,
        });
        const batch = Array.isArray(data?.items) ? data.items : [];
        allItems.push(...batch);
        if (appliedQuery.subject && data?.has_more != null) {
            if (!data.has_more) break;
        } else if (batch.length < pageSize) {
            break;
        }
        page += 1;
    }

    return allItems;
};
